import { Slide } from '@llx/common';
import { Eye } from 'lucide-react';
import { ReactNode, useRef, useEffect, useState, useMemo } from 'react';
import { useSwipeable } from 'react-swipeable';

import { useIsMobile } from '@/hooks/use-mobile';
import { BottomNavigation } from '@/modules/bottom-navigation';
import { SpeakerVideo } from '@/modules/speaker-video';

import MouseScrollIndicator from '../MouseScrollIndicator/MouseScrollIndicator';

/**
 * SlideLayout Component
 *
 * Main layout for displaying masterclass slides.
 * Manages navigation, speaker video display, and progression controls.
 *
 * Features:
 * - Slide title and content display
 * - Speaker video integration without blocking navigation
 * - Bottom navigation bar with progression indicators
 * - Visited slides management for free navigation
 * - Slide access control based on progression and conditions
 *
 * Navigation rules:
 * - The "Next" button is disabled if:
 *   1. An answer is required AND the user hasn't answered
 * - Previously visited slides are always accessible
 * - Videos (intro/outro and speaker) don't prevent navigation
 * - Videos stop automatically when changing slides
 *
 * @example
 * ```tsx
 * <SlideLayout
 *   currentSlide={0}
 *   totalSlides={13}
 *   onSlideChange={handleSlideChange}
 *   onNext={handleNext}
 *   title="Introduction"
 *   visitedSlides={[0, 1]}
 *   maxVisitedSlide={1}
 *   videoUrl="/videos/intro.mp4"
 *   onVideoEnd={handleVideoEnd}
 *   onSpeakerVideoComplete={() => setIsSpeakerVideoComplete(true)}
 *   isSpeakerVideoComplete={false}
 * >
 *   <YourSlideContent />
 * </SlideLayout>
 * ```
 */

interface SlideLayoutProps {
  /** Main slide content */
  children: ReactNode;
  /** Current slide index (0-based) */
  currentSlide: number;
  /** Total number of slides */
  totalSlides: number;
  /** Callback called when slide changes */
  onSlideChange: (slide: number) => void;
  /** Callback called to go to next slide */
  onNext: () => void;
  /** Title displayed at the top of the page */
  title: string;
  /** Optional subtitle */
  subtitle?: string;
  /** Case ID (for display) */
  caseNumber?: string;
  /** Callback to return to home */
  onHomeClick?: () => void;
  /** List of already visited slide IDs */
  visitedSlideIds?: string[];
  /** Current slide ID */
  currentSlideId: string;
  /** List of all slides for ID <-> index conversion */
  slides: Slide[];
  /** Intro/outro modal video URL (doesn't affect navigation) */
  videoUrl?: string | null;
  /** Callback called when modal video ends */
  onVideoEnd?: () => void;
  /** @deprecated Callback called when speaker video ends (no longer affects navigation) */
  onSpeakerVideoComplete?: () => void;
  /** @deprecated Modal video completion state (no longer affects navigation) */
  isVideoComplete?: boolean;
  /** @deprecated Speaker video completion state (no longer affects navigation) */
  isSpeakerVideoComplete?: boolean;
  /** Callback to replay a video */
  onVideoReplay?: (url: string) => void;
  /** Indicates if an answer is required for this slide */
  requiresAnswer: boolean;
  /** Indicates if the user has answered correctly */
  hasAnsweredCorrectly: boolean;
  /** Callback to open anamnesis panel */
  onAnamneseClick?: () => void;
  /** Callback to open documents panel */
  onDocsClick?: () => void;
  /** Number of available support documents */
  docsCount?: number;
  /** Hide speaker video (e.g., during celebration) */
  hideSpeakerVideo?: boolean;
  /** Show reviewer badge in title bar */
  showReviewerBadge?: boolean;
  /** Whether the current user is an accreditation user */
  isAccreditationUser?: boolean;
  /** Whether the current case has been completed */
  isCaseCompleted?: boolean;
}

export const SlideLayout = ({
  children,
  currentSlide,
  totalSlides,
  onSlideChange,
  onNext,
  title,
  subtitle,
  caseNumber = '1',
  onHomeClick,
  visitedSlideIds = [],
  currentSlideId,
  slides,
  videoUrl,
  onVideoEnd,
  onSpeakerVideoComplete,
  onVideoReplay,
  isVideoComplete = true,
  isSpeakerVideoComplete = true,
  requiresAnswer = false,
  hasAnsweredCorrectly,
  onAnamneseClick,
  onDocsClick,
  docsCount = 0,
  hideSpeakerVideo = false,
  showReviewerBadge = false,
  isAccreditationUser = false,
  isCaseCompleted = false,
}: SlideLayoutProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const navigationRef = useRef<HTMLDivElement>(null);
  const titleBarRef = useRef<HTMLDivElement>(null);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [swipeDirection, setSwipeDirection] = useState<'left' | 'right' | null>(null);
  const [swipeDelta, setSwipeDelta] = useState(window.innerWidth * 0.25);
  const [isVerticalSwipe, setIsVerticalSwipe] = useState(false);
  const [isThereScrollableContent, setIsThereScrollableContent] = useState(false);
  const [navHeight, setNavHeight] = useState(0);
  const [titleBarHeight, setTitleBarHeight] = useState(0);
  const [hasUserScrolled, setHasUserScrolled] = useState(false);
  const isProgrammaticScrollRef = useRef(false);
  const isMobile = useIsMobile();

  // Update swipe delta on window resize
  useEffect(() => {
    const handleResize = () => {
      setSwipeDelta(window.innerWidth * 0.25);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Reset swipe state and scroll to top on slide change
  useEffect(() => {
    setSwipeOffset(0);
    setIsTransitioning(false);
    setSwipeDirection(null);
    setHasUserScrolled(false);

    if (scrollContainerRef.current) {
      isProgrammaticScrollRef.current = true;
      scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      document.getElementById('root')?.scrollTo(0, 0);
      // Reset flag after smooth scroll completes
      setTimeout(() => {
        isProgrammaticScrollRef.current = false;
      }, 500);
    }
  }, [currentSlide]);

  // Measure navigation bar height using ResizeObserver for reliability
  // isMobile dependency forces re-observation when switching between mobile/desktop nav
  useEffect(() => {
    if (!navigationRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setNavHeight(entry.target.clientHeight);
      }
    });

    resizeObserver.observe(navigationRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [isMobile]);

  // Measure title bar height using ResizeObserver
  useEffect(() => {
    if (!titleBarRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const rect = entry.target.getBoundingClientRect();
        setTitleBarHeight(rect.top + rect.height);
      }
    });

    resizeObserver.observe(titleBarRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [titleBarRef.current]);

  // Check if there's more content to scroll
  // On mobile, #root is the scroll container due to CSS, not scrollContainerRef
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    // On mobile, the actual scroll container is #root (due to mobile CSS)
    const rootElement = document.getElementById('root');
    const effectiveScrollContainer = isMobile && rootElement ? rootElement : scrollContainer;

    const checkScrollable = () => {
      const { scrollHeight, clientHeight, scrollTop } = effectiveScrollContainer;
      const hasMoreContent =
        scrollHeight > clientHeight && scrollTop + clientHeight < scrollHeight - 10;

      setIsThereScrollableContent(hasMoreContent);

      // Hide indicator when user scrolls (but ignore programmatic scrolls)
      if (scrollTop > 0 && !isProgrammaticScrollRef.current) {
        setHasUserScrolled(true);
      }
    };

    // Use ResizeObserver to detect content dimension changes (important for mobile)
    const resizeObserver = new ResizeObserver(() => {
      checkScrollable();
    });
    resizeObserver.observe(effectiveScrollContainer);

    // Also observe children container for content changes
    const childrenContainer = scrollContainer.querySelector('main');
    if (childrenContainer) {
      resizeObserver.observe(childrenContainer);
    }

    checkScrollable();
    effectiveScrollContainer.addEventListener('scroll', checkScrollable);
    window.addEventListener('resize', checkScrollable);

    return () => {
      resizeObserver.disconnect();
      effectiveScrollContainer.removeEventListener('scroll', checkScrollable);
      window.removeEventListener('resize', checkScrollable);
    };
  }, [children, currentSlide, isMobile]);

  /**
   * Determines if a slide is accessible for navigation.
   *
   * Logic:
   * - Previously visited slides are always accessible
   * - Current slide is always accessible
   * - Next slide (not visited) is accessible if:
   *   1. User can leave current slide (no video playing, no missing answer)
   *   2. Slide is within allowed progression range (maxVisitedSlide + 1)
   * - All other unvisited slides are locked
   */
  const isSlideAccessible: Record<string, boolean> = useMemo(() => {
    if (isAccreditationUser && isCaseCompleted) {
      return slides.reduce(
        (acc, curr) => {
          acc[curr.id] = true;
          return acc;
        },
        {} as Record<string, boolean>
      );
    }

    return slides.reduce(
      (acc, curr) => {
        const slideId = curr.id;
        const slideIndex = slides.findIndex((s) => s.id === slideId);

        // Previously visited slides or current slide → always accessible
        const hasBeenVisited = visitedSlideIds.includes(slideId);
        if (hasBeenVisited || slideId === currentSlideId) {
          acc[slideId] = true;
          return acc;
        }

        // For the next slide (not yet visited)
        if (slideIndex === currentSlide + 1) {
          // Check that user can leave current slide
          const canLeaveCurrentSlide = !(requiresAnswer && !hasAnsweredCorrectly);

          // Check that slide is within allowed general progression
          // Calculate maximum index of visited slides
          const maxVisitedIndex = visitedSlideIds
            .map((id) => slides.findIndex((s) => s.id === id))
            .filter((idx) => idx !== -1)
            .reduce((max, idx) => Math.max(max, idx), 0);

          const isWithinProgressRange = slideIndex <= maxVisitedIndex + 1;

          acc[slideId] = canLeaveCurrentSlide && isWithinProgressRange;
          return acc;
        }

        // All other unvisited slides → locked
        acc[slideId] = false;
        return acc;
      },
      {} as Record<string, boolean>
    );
  }, [
    slides,
    visitedSlideIds,
    currentSlide,
    currentSlideId,
    hasAnsweredCorrectly,
    requiresAnswer,
    isAccreditationUser,
    isCaseCompleted,
  ]);

  const isLastSlide = currentSlide === totalSlides - 1;

  // Check if current slide was already visited before
  const currentSlideObj = slides[currentSlide];
  const wasSlideAlreadyVisited = currentSlideObj
    ? visitedSlideIds.includes(currentSlideObj.id)
    : false;

  /**
   * Disable Next button only if:
   * - An answer is required AND user hasn't answered yet
   *
   * Note: Videos no longer block navigation.
   * They stop automatically when changing slides.
   */
  const isNextDisabled = requiresAnswer && !hasAnsweredCorrectly;

  const handlePrev = () => {
    if (currentSlide > 0) {
      onSlideChange(currentSlide - 1);
    }
  };

  // Calculate swipe offset with resistance for blocked directions
  const calculateSwipeOffset = (deltaX: number, direction: 'left' | 'right') => {
    const canSwipeLeft = !isNextDisabled && currentSlide < totalSlides - 1;
    const canSwipeRight = currentSlide > 0;

    // Apply resistance if swipe is not allowed
    if (direction === 'left' && !canSwipeLeft) {
      return deltaX * 0.2; // 80% resistance
    }
    if (direction === 'right' && !canSwipeRight) {
      return deltaX * 0.2;
    }

    // Normal swipe with slight natural resistance
    return deltaX * 0.8;
  };

  // Swipe handlers for mobile navigation with visual feedback
  const swipeHandlers = useSwipeable({
    onSwiping: (eventData) => {
      // Detect swipe direction on first movement
      if (!isVerticalSwipe && swipeOffset === 0) {
        const absX = Math.abs(eventData.deltaX);
        const absY = Math.abs(eventData.deltaY);

        // If vertical movement is greater, mark as vertical swipe
        if (absY > absX) {
          setIsVerticalSwipe(true);
          return;
        }
      }

      // If vertical swipe detected, don't apply horizontal offset
      if (isVerticalSwipe) {
        return;
      }

      // Update offset in real-time during swipe
      const direction = eventData.deltaX > 0 ? 'right' : 'left';
      const offset = calculateSwipeOffset(eventData.deltaX, direction);
      setSwipeOffset(offset);
    },
    onSwipedLeft: () => {
      // Ignore if vertical swipe
      if (isVerticalSwipe) {
        setIsVerticalSwipe(false);
        return;
      }

      if (!isNextDisabled && currentSlide < totalSlides - 1) {
        setIsTransitioning(true);
        setSwipeDirection('left');
        // Continue animation to the left (complete exit)
        setSwipeOffset(-window.innerWidth);

        // Change slide during animation
        setTimeout(() => {
          onNext();
          setSwipeOffset(0);
          setIsTransitioning(false);
          setSwipeDirection(null);
        }, 300); // CSS transition duration
      } else {
        // Bounce back if blocked
        setIsTransitioning(true);
        setSwipeOffset(0);
        setTimeout(() => setIsTransitioning(false), 300);
      }
    },
    onSwipedRight: () => {
      // Ignore if vertical swipe
      if (isVerticalSwipe) {
        setIsVerticalSwipe(false);
        return;
      }

      if (currentSlide > 0) {
        setIsTransitioning(true);
        setSwipeDirection('right');
        // Continue animation to the right (complete exit)
        setSwipeOffset(window.innerWidth);

        setTimeout(() => {
          handlePrev();
          setSwipeOffset(0);
          setIsTransitioning(false);
          setSwipeDirection(null);
        }, 300);
      } else {
        setIsTransitioning(true);
        setSwipeOffset(0);
        setTimeout(() => setIsTransitioning(false), 300);
      }
    },
    delta: swipeDelta, // Minimum swipe distance (25% of screen width)
    preventScrollOnSwipe: false, // Allow vertical scroll
    trackMouse: false, // Disable on desktop
    trackTouch: true, // Enable on mobile only
  });

  return (
    <div className="relative flex h-full flex-col bg-background">
      {/* Speaker video overlay (fixed, stays visible during scroll) */}
      {!hideSpeakerVideo && (
        <div
          className="fixed right-4 z-50 md:right-8"
          style={{ top: `calc(${titleBarHeight}px + 16px)` }}
        >
          <SpeakerVideo
            videoUrl={videoUrl ?? null}
            onVideoEnd={onVideoEnd ?? (() => {})}
            onVideoComplete={onSpeakerVideoComplete}
            onReplay={onVideoReplay}
          />
        </div>
      )}

      {/* Scrollable container with content - Title bar INSIDE for Safari sticky compatibility */}
      <div
        {...swipeHandlers}
        ref={scrollContainerRef}
        className="relative flex-1 touch-pan-y overflow-y-auto overflow-x-hidden"
        style={{
          // Only apply transform when swiping to avoid creating containing block for fixed elements
          ...(swipeOffset !== 0 && { transform: `translateX(${swipeOffset}px)` }),
          transition: isTransitioning ? 'transform 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)' : 'none',
          willChange: swipeOffset !== 0 ? 'transform' : 'auto',
        }}
      >
        {/* Title bar - Sticky INSIDE scrollable container for Safari compatibility */}
        <div
          ref={titleBarRef}
          className="sticky top-0 z-40 flex-shrink-0 border-b border-border bg-card px-3 py-3 shadow-sm sm:px-6 sm:py-4"
        >
          <div className="flex items-center justify-between gap-2">
            <div className="flex-1">
              <h1 className="text-lg font-semibold text-card-foreground sm:text-2xl">{title}</h1>
              {subtitle && <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>}
            </div>
            {showReviewerBadge && (
              <div className="flex items-center gap-1.5 rounded-full bg-livelinx-purple200/90 px-3 py-1.5 shadow-md">
                <Eye className="h-3.5 w-3.5 text-white" />
                <span className="font-body text-xs font-medium text-white">Reviewer</span>
              </div>
            )}
          </div>
        </div>

        <div className="h-full">
          <main className="px-4 pb-[calc(8rem+env(safe-area-inset-bottom,0px))] pt-4 sm:px-8 sm:pt-6 md:pb-28">
            {children}
          </main>
        </div>
      </div>

      {/* Scroll indicator gradient - positioned dynamically above bottom navigation */}
      <div
        className={`pointer-events-none fixed left-0 right-0 z-30 h-24 bg-gradient-to-t from-livelinx-darkgrey/20 to-transparent transition-opacity duration-300 ${
          isThereScrollableContent && navHeight > 0 ? 'opacity-100' : 'opacity-0'
        }`}
        style={{ bottom: `${navHeight}px` }}
      />
      <div
        className="fixed right-4 z-40 sm:left-[calc(50%+33rem)] sm:right-auto"
        style={{ bottom: `calc(${navHeight}px + 16px)` }}
      >
        {isThereScrollableContent && navHeight > 0 && !hasUserScrolled && (
          <MouseScrollIndicator key={currentSlide} scrollRef={scrollContainerRef} />
        )}
      </div>

      {/* Bottom navigation */}
      <BottomNavigation
        ref={navigationRef}
        currentSlide={currentSlide}
        totalSlides={totalSlides}
        onSlideChange={onSlideChange}
        onNext={onNext}
        onPrevious={handlePrev}
        onHomeClick={onHomeClick}
        visitedSlideIds={visitedSlideIds}
        currentSlideId={currentSlideId}
        slides={slides}
        isNextDisabled={isNextDisabled}
        isLastSlide={isLastSlide}
        onAnamneseClick={onAnamneseClick}
        onDocsClick={onDocsClick}
        docsCount={docsCount}
        isSlideAccessible={isSlideAccessible}
        isAccreditationUser={isAccreditationUser}
        isCaseCompleted={isCaseCompleted}
      />
    </div>
  );
};
